import React from "react";
import moment from "moment";
import { Link } from "react-router-dom";
import { Popover } from "antd";
import { intersection, pullAll } from "lodash";

import { DATETIME_FORMAT_FRONTEND, DATE_FORMAT_FRONTEND } from "../../constants";
import TableBase from "./Base";
import { getTranslation } from "../../utils/helper";

const OrdersTable = ({ scrollHeight, actionWidth, withAction, setting, roles = [], routePlanType, ...props }) => {
  let COLUMNS = [
    {
      title: getTranslation(setting, "Orders", "Order ID"),
      dataIndex: "orderCode",
      width: 150,
      ellipsis: true,
      fixed: true,
      render(value, record) {
        return (
          <Link to={`/orders/${record._id}`} target="_blank">
            {value}
          </Link>
        );
      },
    },
    {
      title: getTranslation(setting, "Orders", "Order Ref"),
      dataIndex: "orderRef",
      width: 150,
      ellipsis: true,
      fixed: true,
      render(_value, record) {
        return record.orderRef || record.orderCode;
      },
    },
    {
      title: getTranslation(setting, "Orders", "Date"),
      dataIndex: "deliveryDate",
      width: 180,
      ellipsis: true,
      render(value) {
        return value ? moment(value).utc().locale(setting.systemLanguage).format(DATE_FORMAT_FRONTEND) : "-";
      },
    },
    {
      title: getTranslation(setting, "Orders", "Pickup Name"),
      dataIndex: "pickupName",
      width: 150,
      ellipsis: true,
    },
    {
      title: getTranslation(setting, "Orders", "Pickup Address"),
      dataIndex: "pickupAddress",
      width: 250,
      ellipsis: true,
    },
    {
      title: getTranslation(setting, "Orders", "Pickup Phone"),
      dataIndex: "pickupPhone",
      width: 150,
      ellipsis: true,
    },
    {
      title: getTranslation(setting, "Orders", "Pickup Time Window"),
      dataIndex: "pickupTimeWindow",
      width: 200,
      ellipsis: true,
      render(_value, record) {
        return (record.timeStart || "") + " - " + (record.timeEnd || "");
      },
    },
    {
      title: getTranslation(setting, "Orders", "Delivery Name"),
      dataIndex: "deliveryName",
      width: 150,
      ellipsis: true,
    },
    {
      title: getTranslation(setting, "Orders", "Delivery Address"),
      dataIndex: "deliveryAddress",
      width: 250,
      ellipsis: true,
    },
    {
      title: getTranslation(setting, "Orders", "Delivery Phone"),
      dataIndex: "deliveryPhone",
      width: 150,
      ellipsis: true,
    },
    {
      title: getTranslation(setting, "Orders", "Delivery Email"),
      dataIndex: "deliveryEmail",
      width: 150,
      ellipsis: true,
      render(value) {
        return <a href={`mailto:${value}`}>{value}</a>;
      },
    },
    {
      title: getTranslation(setting, "Orders", "Delivery Time Window"),
      dataIndex: "deliveryTimeWindow",
      width: 200,
      ellipsis: true,
      render(_value, record) {
        return (record.deliveryTimeStart || "") + " - " + (record.deliveryTimeEnd || "");
      },
    },
    {
      title: getTranslation(setting, "Orders", "User Group"),
      dataIndex: ["group", "name"],
      width: 150,
      ellipsis: true,
    },
    {
      title: getTranslation(setting, "Orders", "Load"),
      dataIndex: "load",
      width: 100,
      ellipsis: true,
    },
    {
      title: getTranslation(setting, "Orders", "Date Added"),
      dataIndex: "createdAt",
      width: 180,
      ellipsis: true,
      render(value) {
        return value ? moment(value).locale(setting.systemLanguage).format(DATE_FORMAT_FRONTEND) : "-";
      },
    },
    {
      title: getTranslation(setting, "Orders", "Updated At"),
      dataIndex: "updatedAt",
      width: 240,
      render(value) {
        return value ? moment(value).locale(setting.systemLanguage).format(DATETIME_FORMAT_FRONTEND) : "-";
      },
    },
    {
      title: getTranslation(setting, "Orders", "Status"),
      dataIndex: "status",
      width: 130,
      fixed: "right",
      render(status, record) {
        const errorDetails = [...record.errorDetails];
        pullAll(errorDetails, [
          "Pickup lat must be a latitude string or number",
          "Pickup lng must be a longitude string or number",
          "Delivery lat must be a latitude string or number",
          "Delivery lng must be a longitude string or number",
        ]);
        return (
          <>
            {record.errorDetails.length ? (
              <Popover
                title={getTranslation(setting, "Orders", "Error details")}
                content={
                  <div className="popupErrorMessages">
                    {errorDetails.map((error, index) => {
                      return (
                        <div key={index} className="item">
                          {error}
                        </div>
                      );
                    })}
                    {intersection(record.errorDetails, [
                      "Pickup lat must be a latitude string or number",
                      "Pickup lng must be a longitude string or number",
                    ]).length > 0 && (
                      <div key="Pickup Address" className="item">
                        Pickup address is not valid
                      </div>
                    )}
                    {intersection(record.errorDetails, [
                      "Delivery lat must be a latitude string or number",
                      "Delivery lng must be a longitude string or number",
                    ]).length > 0 && (
                      <div key="Delivery Address" className="item">
                        Delivery address is not valid
                      </div>
                    )}
                  </div>
                }>
                <span className={"status orderStatus " + status.toLowerCase()}>
                  {getTranslation(setting, "Orders", status)}
                </span>
              </Popover>
            ) : (
              <span className={"status orderStatus " + status.toLowerCase()}>
                {getTranslation(setting, "Orders", status)}
              </span>
            )}
          </>
        );
      },
    },
  ];

  if (roles.includes("admin")) {
    COLUMNS.splice(2, 0, {
      title: "Company Admin Email",
      dataIndex: "companyName",
      width: 300,
      ellipsis: true,
      render(_value, record) {
        return record.userAdmin?.email || "-";
      },
    });
  }

  if (withAction) {
    COLUMNS.push({
      key: "action",
      width: actionWidth || 100,
      fixed: "right",
      render(record) {
        return withAction(record);
      },
    });
  }

  if (routePlanType !== "PDP") {
    COLUMNS = COLUMNS.filter(
      i => !["pickupName", "pickupAddress", "pickupPhone", "pickupTimeWindow"].includes(i.dataIndex),
    );
  }

  if (scrollHeight) {
    return <TableBase columns={COLUMNS} scroll={{ x: 991, y: scrollHeight }} {...props} />;
  } else {
    return <TableBase columns={COLUMNS} scroll={{ x: 991 }} {...props} />;
  }
};

export default OrdersTable;
